import { useState, useEffect } from 'react';
import { Shield, Clock, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBreakGlassSession } from '@/hooks/useBreakGlassSession';
import { useOrganization } from '@/contexts/OrganizationContext';

/**
 * Persistent banner shown when the current user is in a break glass org context.
 * Displays a countdown timer and provides a "Return to home org" button.
 * Turns amber at < 5 minutes remaining.
 */
export function BreakGlassCountdownBanner() {
  const { organization, returnToMspOrg } = useOrganization();
  const { active, expiresAt, role, status } = useBreakGlassSession({
    organizationId: organization?.id,
  });
  const [, setTick] = useState(0);

  // Tick every second for countdown
  useEffect(() => {
    if (!active || !expiresAt) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [active, expiresAt]);

  // Redirect when session is revoked or expired (status present but no longer active)
  useEffect(() => {
    if (!active && status && (status === 'revoked' || status === 'expired')) {
      returnToMspOrg();
    }
  }, [active, status, returnToMspOrg]);

  if (!active || !expiresAt) return null;

  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();

  if (diffMs <= 0) {
    // Session expired — redirect back to home org
    returnToMspOrg();
    return null;
  }

  const hours = Math.floor(diffMs / 3600000);
  const minutes = Math.floor((diffMs % 3600000) / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);
  const isWarning = diffMs < 5 * 60 * 1000;
  const isCritical = diffMs < 60 * 1000;

  const bgClass = isCritical
    ? 'bg-destructive text-destructive-foreground'
    : isWarning
      ? 'bg-amber-500 text-white dark:bg-amber-600'
      : 'bg-blue-600 text-white dark:bg-blue-700';

  const timeStr = hours > 0
    ? `${hours}h ${minutes}m ${seconds}s`
    : `${minutes}m ${seconds}s`;

  return (
    <div className={`sticky top-0 z-50 flex items-center justify-between px-4 py-2 ${bgClass}`}>
      <div className="flex items-center gap-3">
        <Shield className="h-4 w-4" />
        <span className="text-sm font-medium">
          Break glass access
          {role && <span className="opacity-80"> ({role})</span>}
        </span>
        <div className="flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          <span className="font-mono text-sm font-bold">{timeStr}</span>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 gap-1 text-current hover:bg-white/20"
        onClick={() => {
          returnToMspOrg();
        }}
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Return to home
      </Button>
    </div>
  );
}
